import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { useStaffWorkspace } from "../../../app/session";
import { ErrorState, Loading } from "../../../components/States";
import { Dialog, KopfMehr, PageHead, useAction } from "../../../components/ui";
import { PdfDesignLink } from "../../../components/PdfDesignLink";
import { date } from "../../../lib/format";
import { loadVorlage, loadVorlagen, newTemplateVersion, type TemplateRow } from "./daten";
import { slugKey } from "./VariablenEditor";
import { ROLE_LABEL } from "./modell";
import { Vertragseinstellungen } from "./VorlagenZusatz";
import { Galerie } from "./baukasten/Galerie";
import { musterKennung, musterLandHinweis, musterZuVorlage, useMuster } from "./MusterHinweis";
import { t, nameIn, sprachName, ta } from "../../../lib/sprache";
import { VorlagenVorschau, useVorschauParam } from "./VertragVorschau";

/** Grundgerüst einer neuen Vorlage in ihrer Sprache (VERTRAEGE-13). */
export const STARTER: Record<"de" | "en", string> = {
  de: `<h1>Vertragstitel</h1>

<p>Vertragsnummer {{contract.number}} · Datum {{contract.date|date}}</p>

<p>zwischen <strong>{{agency.legal_name}}</strong>, {{agency.address_block}}, und <strong>{{talent.full_name}}</strong>, {{talent.address_block}}</p>

<h2>§ 1 Gegenstand</h2>

<p>Beschreibe hier den Gegenstand des Vertrags.</p>

<h2>§ 2 Schlussbestimmungen</h2>

<ol>
  <li>Änderungen bedürfen der Textform (z. B. E-Mail).</li>
  <li>Es gilt {{agency.governing_law}}. Gerichtsstand ist, soweit gesetzlich zulässig, {{agency.jurisdiction}}.</li>
</ol>

<h2>Unterschriften</h2>

{{signature:agency}}

{{signature:talent}}`,
  en: `<h1>Agreement title</h1>

<p>Contract number {{contract.number}} · Date {{contract.date|date}}</p>

<p>between <strong>{{agency.legal_name}}</strong>, {{agency.address_block}}, and <strong>{{talent.full_name}}</strong>, {{talent.address_block}}</p>

<h2>§ 1 Subject matter</h2>

<p>Describe the subject matter of the agreement here.</p>

<h2>§ 2 Final provisions</h2>

<ol>
  <li>Amendments must be made in text form (e.g. e-mail).</li>
  <li>This agreement is governed by {{agency.governing_law}}. Place of jurisdiction, to the extent permitted by law, is {{agency.jurisdiction}}.</li>
</ol>

<h2>Signatures</h2>

{{signature:agency}}

{{signature:talent}}`,
};

/** Vertragsvorlagen je Vertragsart mit Versionen; Admins legen an, bearbeiten, aktivieren und archivieren. */
export function Vorlagen() {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["contract_templates", orgId], queryFn: () => loadVorlagen(api, orgId) });
  const [dialog, setDialog] = useState<"vorlage" | "art" | { kind: "art-bearbeiten"; id: string } | null>(null);
  const [name, setName] = useState("");
  const [lang, setLang] = useState<"de" | "en">("de");
  const [kindId, setKindId] = useState("");
  const [copyFrom, setCopyFrom] = useState("");
  const [kindDe, setKindDe] = useState("");
  const [kindEn, setKindEn] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const { busy, error, run, attempt } = useAction();
  // 0181 (Anwalt Punkt 13): Kennung der übernommenen CreatorOffice-Muster (Rechtsordnung, Version, Stand)
  const muster = useMuster();
  // K-511: Vorschau jeder Vorlage mit Beispielwerten, direkt aus der Liste
  const vorschau = useVorschauParam();

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const { templates, kinds } = q.data;

  // Familien: je Kennung und Sprache die aktive Fassung, ein offener Entwurf und die Zahl archivierter Fassungen
  const families = new Map<string, TemplateRow[]>();
  for (const t of templates) {
    const k = `${t.key}|${t.language}`;
    families.set(k, [...(families.get(k) ?? []), t]);
  }
  const groups = [...kinds.filter((k) => showArchived || !k.archived_at).map((k) => ({ id: k.id as string | null, name: nameIn(k.name_de, k.name_en), en: k.name_en, archived: !!k.archived_at })),
    { id: null, name: "Ohne Vertragsart", en: "", archived: false }];
  // Vertragsart archivieren (Befund 31): nur, wenn keine aktive Vorlage und kein offener Entwurf sie nutzt
  const kindInUse = (id: string) => templates.some((t) => t.kind_id === id && t.status !== "archived");

  async function createTemplate(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length < 3) return;
    const base = slugKey(name);
    let key = base;
    for (let i = 2; templates.some((t) => t.key === key && t.language === lang); i++) key = `${base}_${i}`;
    const src = copyFrom ? await loadVorlage(api, orgId, copyFrom) : null;
    const rows = await run("anlegen", () => api.insert<{ id: string }>("contract_templates", {
      org_id: orgId, key, name: name.trim(), language: lang, kind_id: kindId || src?.kind_id || null, status: "draft",
      body_html: src?.body_html ?? STARTER[lang], annex_html: src?.annex_html ?? null, variables: src?.variables ?? [],
      required_signers: src?.required_signers ?? ["agency", "talent"], annex_hidden_from: src?.annex_hidden_from ?? [],
      // Beschreibung in der Sprache der Vorlage (sie steht in der Vorlagenliste, auch in der anderen Oberflächensprache)
      description: src ? (lang === "en" ? `Copy of “${src.name}”` : `Kopie von „${src.name}“`) : null,
    }, { columns: "id" }), t("Vorlage angelegt.", "Template created."));
    if (rows?.[0]) navigate(`/agentur/vertraege/vorlagen/${rows[0].id}`);
  }

  async function saveKind(e: FormEvent) {
    e.preventDefault();
    if (kindDe.trim().length < 2) return;
    const editing = typeof dialog === "object" && dialog?.kind === "art-bearbeiten" ? dialog.id : null;
    const ok = editing
      ? await attempt("art", () => api.update("contract_kinds", [["id", "eq", editing]], { name_de: kindDe.trim(), name_en: kindEn.trim() || kindDe.trim() }), t("Vertragsart gespeichert.", "Contract type saved."))
      : await attempt("art", () => api.insert("contract_kinds", { org_id: orgId, key: slugKey(kindDe).slice(0, 40).padEnd(2, "_"), name_de: kindDe.trim(), name_en: kindEn.trim() || kindDe.trim(), sort_order: kinds.length + 1 }), t("Vertragsart angelegt.", "Contract type created."));
    if (ok) { setDialog(null); setKindDe(""); setKindEn(""); }
  }

  return (
    <main id="main" className="content">
      <PageHead title={t("Vertragsvorlagen", "Contract templates")} eyebrow={ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")} text={t("Jede Änderung einer aktiven Vorlage wird eine neue Version. Bestehende Verträge behalten ihre Fassung.", "Every change to an active template becomes a new version. Existing contracts keep their version.")}>
        {/* Prüf-Chat 30.10.2026: eine Hauptaktion, die übrigen hinter „Mehr“ (wie bei den Verträgen), keine Zeile mit Einzelknopf */}
        {ws.isChef && <button className="primary" onClick={() => setDialog("vorlage")}>{t("Neue Vorlage", "New template")}</button>}
        <PdfDesignLink art="contract" />
        <KopfMehr>
          <Link to="/agentur/vertraege">{t("Zu den Verträgen", "To the contracts")}</Link>
          <Link to="/agentur/vertraege/klauseln">{t("Klausel-Bibliothek", "Clause library")}</Link>
          {ws.isChef && <button type="button" onClick={() => setDialog("art")}>{t("Vertragsart anlegen", "Create contract type")}</button>}
          {ws.can("edit_templates") && <Link to="/agentur/vertraege/vorlagen/import">{t("Eigenen Vertrag übernehmen", "Import your own contract")}</Link>}
        </KopfMehr>
      </PageHead>
      <div className="toolbar">
        <label className="row"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />{t("Archivierte Vorlagen und Vertragsarten zeigen", "Show archived templates and contract types")}</label>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
      {groups.map((g) => {
        const fams = [...families.values()].filter((f) => (f[0]!.kind_id ?? null) === g.id);
        const visible = fams.filter((f) => showArchived || f.some((t) => t.status !== "archived"));
        if (!visible.length && g.id === null) return null;
        return (
          <section key={g.id ?? "ohne"} className="panel stack" aria-label={g.name}>
            <div className="row between">
              <h3>{g.name}{g.en && g.en !== g.name ? <small> · {g.en}</small> : null}{g.archived ? <small> {t("· archiviert", "· archived")}</small> : null}</h3>
              {ws.isChef && g.id && (
                <span className="row">
                  <button type="button" onClick={() => { const k = kinds.find((x) => x.id === g.id)!; setKindDe(k.name_de); setKindEn(k.name_en); setDialog({ kind: "art-bearbeiten", id: g.id! }); }}>{t("Umbenennen", "Rename")}</button>
                  {g.archived
                    ? <button type="button" disabled={!!busy} onClick={() => void attempt(`art-${g.id}`, () => api.update("contract_kinds", [["id", "eq", g.id!]], { archived_at: null }, { columns: "id" }), t("Vertragsart wiederhergestellt.", "Contract type restored."))}>{t("Wiederherstellen", "Restore")}</button>
                    : <button type="button" disabled={!!busy || kindInUse(g.id)} title={kindInUse(g.id) ? t("Eine aktive Vorlage oder ein Entwurf nutzt diese Art.", "An active template or a draft uses this type.") : undefined}
                        onClick={() => void attempt(`art-${g.id}`, () => api.update("contract_kinds", [["id", "eq", g.id!]], { archived_at: new Date().toISOString() }, { columns: "id" }), t("Vertragsart archiviert. Sie steht in keiner Auswahl mehr.", "Contract type archived. It no longer appears in any selection."))}>{t("Archivieren", "Archive")}</button>}
                </span>
              )}
            </div>
            {visible.length === 0 && <p>{t("Noch keine Vorlage dieser Art.", "No template of this type yet.")}{ws.isChef && g.id && !g.archived ? t(" Nicht mehr gebraucht? Archivieren.", " No longer needed? Archive it.") : ""}</p>}
            {visible.map((f) => {
              const active = f.find((t) => t.status === "active");
              const draft = f.find((t) => t.status === "draft");
              const archived = f.filter((t) => t.status === "archived").length;
              const head = active ?? draft ?? f[0]!;
              return (
                <div key={`${head.key}|${head.language}`} className="task tk-vorlage-zeile">
                  <div>
                    <strong><Link to={`/agentur/vertraege/vorlagen/${head.id}`}>{head.name}</Link></strong>
                    <p>
                      {sprachName(head.language)} · {active ? t(`aktiv in Version ${active.version}`, `active in version ${active.version}`) : t("nicht aktiv", "not active")}
                      {draft ? t(` · Entwurf Version ${draft.version}`, ` · draft version ${draft.version}`) : ""}{archived ? t(` · ${archived} archiviert`, ` · ${archived} archived`) : ""}
                      {" · "}{t("unterschreiben: ", "signing: ")}{head.required_signers.map((r) => ROLE_LABEL[r]).join(", ")} {t("· geändert ", "· changed ")}{date(head.updated_at, ws.org.timezone)}
                    </p>
                    {head.description && <small>{head.description}</small>}
                    {(() => {
                      const m = musterZuVorlage(muster.zeilen, head);
                      return m ? <small className="tk-muster-kennung">{musterKennung(m, head.platform_version)}{t(" · unverbindliches Muster, vor dem Einsatz rechtlich prüfen", " · non-binding sample, have it legally reviewed before use")}{musterLandHinweis(m.jurisdiction, muster.country) ? ` · ${musterLandHinweis(m.jurisdiction, muster.country)}` : ""}</small> : null;
                    })()}
                  </div>
                  <div className="row">
                    <button type="button" onClick={() => vorschau.open(head.id)} aria-label={t(`Vorschau: ${head.name}`, `Preview: ${head.name}`)}>{t("Vorschau", "Preview")}</button>
                    {draft && !active && head.platform_version != null && !ws.isChef && <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Muster: prüfen und aktivieren", "Sample: review and activate")}</span>}
                    {(() => { const m = musterZuVorlage(muster.zeilen, head); return m && head.platform_version != null && m.version > head.platform_version
                      ? <span className="pill warning">{t(`neuere Fassung (Version ${m.version})`, `newer version (version ${m.version})`)}</span> : null; })()}
                    {draft && ws.can("edit_templates") && <Link className="btn" to={`/agentur/vertraege/vorlagen/${draft.id}/baukasten`}>{t("Im Baukasten", "In the builder")}</Link>}
                    {draft && ws.isChef && <Link className="btn primary" to={`/agentur/vertraege/vorlagen/${draft.id}`}>{!active && head.platform_version != null ? t("Prüfen und aktivieren", "Review and activate") : t("Entwurf bearbeiten", "Edit draft")}</Link>}
                    {!draft && active && active.blocks_schema == null && ws.can("edit_templates") && (
                      <button type="button" disabled={!!busy} title={t("Legt einen Entwurf der nächsten Version in Bausteinen an. Die aktive Version bleibt, bis du den Entwurf aktivierst.", "Creates a draft of the next version in blocks. The active version stays until you activate the draft.")}
                        onClick={async () => { const id = await run("umwandeln", () => newTemplateVersion(api, active.id)); if (id) navigate(`/agentur/vertraege/vorlagen/${id}/baukasten`); }}>{t("In Bausteine umwandeln", "Convert to blocks")}</button>
                    )}
                    {!draft && active && ws.isChef && (
                      <button type="button" disabled={!!busy} onClick={async () => { const id = await run("version", () => newTemplateVersion(api, active.id)); if (id) navigate(`/agentur/vertraege/vorlagen/${id}`); }}>{t("Neue Version", "New version")}</button>
                    )}
                    {active && <Link className="btn" to={`/agentur/vertraege/neu?vorlage=${encodeURIComponent(active.id)}`}>{t("Vertrag erstellen", "Create contract")}</Link>}
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}

      <Galerie templates={templates} orgId={orgId} darf={ws.can("edit_templates")} />
      <VorlagenVorschau templateId={vorschau.id && templates.some((x) => x.id === vorschau.id) ? vorschau.id : null} onClose={vorschau.close} />
      {ws.isChef && <Vertragseinstellungen orgId={orgId} />}

      <Dialog open={dialog === "vorlage"} onClose={() => setDialog(null)} title={t("Neue Vorlage", "New template")}>
        <form className="stack" onSubmit={(e) => void createTemplate(e)}>
          <label>Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("z. B. Rahmenvertrag Creator", "e.g. creator framework contract")} required minLength={3} /></label>
          <div className="grid">
            <label>
              {t("Sprache", "Language")}<select value={lang} onChange={(e) => setLang(e.target.value as "de" | "en")}><option value="de">{t("Deutsch", "German")}</option><option value="en">{t("Englisch", "English")}</option></select>
            </label>
            <label>
              {t("Vertragsart", "Contract type")}<select value={kindId} onChange={(e) => setKindId(e.target.value)}>
                <option value="">{t("Ohne Vertragsart", "No contract type")}</option>
                {kinds.filter((k) => !k.archived_at).map((k) => <option key={k.id} value={k.id}>{nameIn(k.name_de, k.name_en)}</option>)}
              </select>
            </label>
          </div>
          <label>
            {t("Ausgangspunkt", "Starting point")}<select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">{t("Leere Vorlage mit Grundgerüst (Lücken sind markiert)", "Empty template with basic structure (gaps are marked)")}</option>
              {templates.filter((t) => t.status === "active").map((tl) => <option key={tl.id} value={tl.id}>{t("Kopie von ", "Copy of ")}{tl.name} ({sprachName(tl.language)})</option>)}
            </select>
          </label>
          <small>{t("Du hast den Vertrag schon als Word oder PDF? ", "Already have the contract as Word or PDF? ")}<Link to="/agentur/vertraege/vorlagen/import">{t("Eigenen Vertrag übernehmen", "Import your own contract")}</Link></small>
          <div className="row">
            <button className="primary" type="submit" disabled={!!busy || name.trim().length < 3}>{t("Vorlage anlegen", "Create template")}</button>
            <button type="button" onClick={() => setDialog(null)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </form>
      </Dialog>
      <Dialog open={dialog === "art" || (typeof dialog === "object" && dialog?.kind === "art-bearbeiten")} onClose={() => setDialog(null)} title={dialog === "art" ? t("Vertragsart anlegen", "Create contract type") : t("Vertragsart umbenennen", "Rename contract type")}>
        <form className="stack" onSubmit={(e) => void saveKind(e)}>
          <label>{t("Name (Deutsch)", "Name (German)")}<input value={kindDe} onChange={(e) => setKindDe(e.target.value)} placeholder={t("z. B. Geheimhaltungsvereinbarung", "e.g. Geheimhaltungsvereinbarung")} required minLength={2} /></label>
          <label>{t("Name (Englisch)", "Name (English)")}<input value={kindEn} onChange={(e) => setKindEn(e.target.value)} placeholder={t("z. B. Non-disclosure agreement", "e.g. Non-disclosure agreement")} /></label>
          <div className="row">
            <button className="primary" type="submit" disabled={!!busy || kindDe.trim().length < 2}>{t("Speichern", "Save")}</button>
            <button type="button" onClick={() => setDialog(null)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </form>
      </Dialog>
    </main>
  );
}
